// =============================================================================
// Email — Tenant-level email configuration
//
// Five tabs:
//   1. Identity    — From name, reply-to address
//   2. Email Types — Per-tenant toggle for tenant-relevant email types
//   3. Templates   — Customize email template content per type
//   4. Campaigns   — Send batch emails to B2C users
//   5. Preview     — Select email type and see branded preview in iframe
// =============================================================================

import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Loader2, Save, Mail, List, Eye, Check, X, ShieldOff,
  FileText, Send, ChevronDown, ChevronUp, RotateCcw, Plus,
  Clock, Users, AlertCircle, Palette,
} from 'lucide-react';
import { cn } from '@/utils/helpers';
import {
  fetchTenantEmailSettings,
  updateTenantEmailIdentity,
  updateNotificationPrefs,
  fetchEmailTemplates,
  fetchTenantEmailTemplates,
  upsertTenantEmailTemplate,
  deleteTenantEmailTemplate,
  fetchCampaigns,
  createCampaign,
  sendCampaignNow,
  cancelCampaign,
  fetchCampaignRecipients,
  fetchCampaignsThisMonth,
} from '@/lib/api';
import type { Tenant, EmailTemplate, TenantEmailTemplate, CampaignEmail, CampaignRecipient, CreateCampaignInput } from '@/types';
import { CAMPAIGN_FILTER_LABELS } from '@/lib/constants';

// -- Constants ----------------------------------------------------------------

type Tab = 'identity' | 'email-types' | 'templates' | 'campaigns' | 'preview';

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: 'identity',    label: 'Identity',    icon: <Mail size={14} /> },
  { id: 'email-types', label: 'Email Types', icon: <List size={14} /> },
  { id: 'templates',   label: 'Templates',   icon: <FileText size={14} /> },
  { id: 'campaigns',   label: 'Campaigns',   icon: <Send size={14} /> },
  { id: 'preview',     label: 'Preview',     icon: <Eye size={14} /> },
];

const CATEGORY_ORDER: EmailTemplate['category'][] = ['auth', 'notification', 'system', 'marketing'];

const CATEGORY_LABELS: Record<string, string> = {
  auth: 'Authentication',
  notification: 'Notification',
  system: 'System',
  marketing: 'Marketing',
};

// =============================================================================

export function EmailPage({ tenant }: { tenant: Tenant }) {
  const [activeTab, setActiveTab] = useState<Tab>('identity');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-surface-900">Email</h1>
        <p className="text-sm text-surface-500 mt-1">
          Configure your app's email identity, templates, campaigns, and control which emails are sent
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-surface-200 rounded-xl w-fit">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-all',
              activeTab === tab.id
                ? 'bg-surface-0 text-surface-900 shadow-sm'
                : 'text-surface-500 hover:text-surface-700',
            )}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'identity' && <IdentityTab tenant={tenant} />}
      {activeTab === 'email-types' && <EmailTypesTab tenant={tenant} />}
      {activeTab === 'templates' && <TemplatesTab tenant={tenant} />}
      {activeTab === 'campaigns' && <CampaignsTab tenant={tenant} />}
      {activeTab === 'preview' && <PreviewTab tenant={tenant} />}
    </div>
  );
}

// =============================================================================
// TAB 1 -- Identity
// =============================================================================

function IdentityTab({ tenant }: { tenant: Tenant }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [fromName, setFromName] = useState('');
  const [fromAddress, setFromAddress] = useState('');
  const [replyTo, setReplyTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTenantEmailSettings(tenant.id);
      setFromName(data.email_from_name || '');
      setFromAddress(data.email_from_address || '');
      setReplyTo(data.email_reply_to || '');
    } catch (err) {
      setError((err as Error).message);
    }
    setLoading(false);
  }, [tenant.id]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await updateTenantEmailIdentity(tenant.id, {
        email_from_name: fromName || null,
        email_from_address: fromAddress || null,
        email_reply_to: replyTo || null,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError((err as Error).message);
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-brand-600 dark:text-brand-400" size={24} />
      </div>
    );
  }

  const appName = tenant.branding?.app_name || tenant.name;

  return (
    <div className="glass-panel p-6 max-w-2xl space-y-5">
      <h2 className="text-lg font-semibold text-surface-900">Email Identity</h2>
      <p className="text-xs text-surface-500">
        This name appears as the sender in all emails sent on behalf of your app.
      </p>

      {/* Email Display Name */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-surface-700">Email Display Name</label>
        <input
          type="text"
          value={fromName}
          onChange={e => setFromName(e.target.value)}
          placeholder={appName}
          className="input-field w-full"
        />
        <p className="text-[11px] text-surface-400">
          {fromName
            ? 'This name will appear as the sender in all emails.'
            : <>Currently using your app name: <span className="text-surface-500 font-medium">{appName}</span></>
          }
        </p>
      </div>

      {/* From Email Address */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-surface-700">From Email Address</label>
        <input
          type="email"
          value={fromAddress}
          onChange={e => setFromAddress(e.target.value)}
          placeholder="noreply@yourdomain.com"
          className="input-field w-full"
        />
        <p className="text-[11px] text-surface-400">
          {fromAddress
            ? 'Your domain must be verified in Resend for this address to work.'
            : <>Currently using platform default: <span className="text-surface-500 font-medium">noreply@discoverygem.com</span>. Set a custom address to override.</>
          }
        </p>
      </div>

      {/* Reply-To Address */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-surface-700">Reply-To Address</label>
        <input
          type="email"
          value={replyTo}
          onChange={e => setReplyTo(e.target.value)}
          placeholder="support@yourdomain.com"
          className="input-field w-full"
        />
      </div>

      {/* Error / success */}
      {error && (
        <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
          <X size={12} /> {error}
        </p>
      )}

      {/* Save */}
      <button
        onClick={handleSave}
        disabled={saving}
        className={cn(
          'flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all',
          saved
            ? 'bg-emerald-600 text-white'
            : 'bg-brand-500 hover:bg-brand-600 text-white',
          saving && 'opacity-60 cursor-not-allowed',
        )}
      >
        {saving ? (
          <Loader2 size={14} className="animate-spin" />
        ) : saved ? (
          <Check size={14} />
        ) : (
          <Save size={14} />
        )}
        {saving ? 'Saving...' : saved ? 'Saved' : 'Save Changes'}
      </button>
    </div>
  );
}

// =============================================================================
// TAB 2 -- Email Types
// =============================================================================

function EmailTypesTab({ tenant }: { tenant: Tenant }) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [notificationPrefs, setNotificationPrefs] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [togglingKey, setTogglingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [allTemplates, settings] = await Promise.all([
        fetchEmailTemplates(),
        fetchTenantEmailSettings(tenant.id),
      ]);
      setTemplates(allTemplates);
      setNotificationPrefs(settings.notification_prefs || {});
    } catch (err) {
      setError((err as Error).message);
    }
    setLoading(false);
  }, [tenant.id]);

  useEffect(() => { load(); }, [load]);

  // Filter to tenant-visible email types (both tenant_admin AND end_user)
  const tenantTemplates = useMemo(
    () => templates.filter(t => t.tenant_visible && (t.recipient_type === 'tenant_admin' || t.recipient_type === 'end_user')),
    [templates],
  );

  // Group by category
  const grouped = useMemo(() => {
    const map = new Map<string, EmailTemplate[]>();
    for (const cat of CATEGORY_ORDER) {
      const items = tenantTemplates.filter(t => t.category === cat);
      if (items.length > 0) map.set(cat, items);
    }
    return map;
  }, [tenantTemplates]);

  const isEnabled = (template: EmailTemplate): boolean => {
    const prefKey = template.template_key;
    if (prefKey in notificationPrefs) return notificationPrefs[prefKey];
    return template.enabled_by_default;
  };

  const handleToggle = async (template: EmailTemplate) => {
    // Cannot toggle if platform has disabled this email
    if (!template.is_platform_enabled) return;

    const prefKey = template.template_key;
    const newVal = !isEnabled(template);
    const updatedPrefs = { ...notificationPrefs, [prefKey]: newVal };

    setTogglingKey(prefKey);
    try {
      await updateNotificationPrefs(tenant.id, updatedPrefs);
      setNotificationPrefs(updatedPrefs);
    } catch (err) {
      console.error('Failed to toggle notification pref:', err);
    }
    setTogglingKey(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-brand-600 dark:text-brand-400" size={24} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <X size={32} className="mx-auto text-red-600 dark:text-red-400 mb-3" />
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  if (tenantTemplates.length === 0) {
    return (
      <div className="text-center py-16">
        <Mail size={32} className="mx-auto text-surface-400 mb-3" />
        <p className="text-sm text-surface-500">No email types available.</p>
        <p className="text-xs text-surface-400 mt-1">
          Email templates have not been configured by the platform yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <p className="text-xs text-surface-500">
        Control which emails are sent to your admin team. Emails disabled at the platform level cannot be re-enabled here.
      </p>

      {CATEGORY_ORDER.map(cat => {
        const items = grouped.get(cat);
        if (!items) return null;
        return (
          <div key={cat} className="glass-panel overflow-hidden">
            {/* Category header */}
            <div className="px-5 py-2.5 bg-surface-50 border-b border-surface-200">
              <span className="text-[10px] font-bold uppercase tracking-wider text-surface-500">
                {CATEGORY_LABELS[cat] || cat}
              </span>
            </div>

            {/* Rows */}
            <div className="divide-y divide-surface-200">
              {items.map(template => {
                const platformDisabled = !template.is_platform_enabled;
                const enabled = isEnabled(template);
                const toggling = togglingKey === template.template_key;

                return (
                  <div
                    key={template.id}
                    className={cn(
                      'flex items-center justify-between gap-4 px-5 py-3.5',
                      platformDisabled && 'opacity-50',
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-surface-900">
                          {template.display_name}
                        </span>
                        {platformDisabled && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium bg-surface-200 text-surface-500">
                            <ShieldOff size={10} />
                            Disabled by platform
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-surface-500 mt-0.5 line-clamp-1">
                        {template.description}
                      </p>
                    </div>

                    {/* Toggle */}
                    <button
                      onClick={() => handleToggle(template)}
                      disabled={platformDisabled || toggling}
                      className="relative inline-flex items-center shrink-0"
                      aria-label={enabled ? 'Disable' : 'Enable'}
                    >
                      <div
                        className={cn(
                          'w-9 h-5 rounded-full transition-colors',
                          platformDisabled
                            ? 'bg-surface-200 cursor-not-allowed'
                            : enabled
                              ? 'bg-brand-500'
                              : 'bg-surface-300',
                          toggling && 'opacity-50',
                        )}
                      >
                        <div
                          className={cn(
                            'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform',
                            enabled && !platformDisabled ? 'translate-x-4' : 'translate-x-0.5',
                          )}
                        />
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// TAB 3 -- Templates
// =============================================================================

function TemplatesTab({ tenant }: { tenant: Tenant }) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [overrides, setOverrides] = useState<TenantEmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Record<string, Record<string, string>>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [allTemplates, tenantOverrides] = await Promise.all([
        fetchEmailTemplates(),
        fetchTenantEmailTemplates(tenant.id),
      ]);
      setTemplates(allTemplates.filter(t => t.tenant_visible && (t.recipient_type === 'tenant_admin' || t.recipient_type === 'end_user')));
      setOverrides(tenantOverrides);
    } catch (err) {
      setError((err as Error).message);
    }
    setLoading(false);
  }, [tenant.id]);

  useEffect(() => { load(); }, [load]);

  const getOverride = (key: string) => overrides.find(o => o.template_key === key);

  const handleExpand = (key: string) => {
    if (expandedKey === key) {
      setExpandedKey(null);
      return;
    }
    setExpandedKey(key);
    // Initialize form with override values or empty strings
    const override = getOverride(key);
    setEditForm(prev => ({
      ...prev,
      [key]: {
        subject: override?.subject || '',
        heading: override?.heading || '',
        subtitle: override?.subtitle || '',
        body: override?.body || '',
        cta_text: override?.cta_text || '',
        cta_color: override?.cta_color || '',
      },
    }));
  };

  const updateField = (key: string, field: string, value: string) => {
    setEditForm(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: value },
    }));
  };

  const handleSave = async (templateKey: string) => {
    setSaving(templateKey);
    setError(null);
    try {
      const form = editForm[templateKey] || {};
      const updates: Record<string, string | null> = {};
      // Only send non-empty fields
      if (form.subject) updates.subject = form.subject;
      if (form.heading) updates.heading = form.heading;
      if (form.subtitle) updates.subtitle = form.subtitle;
      if (form.body) updates.body = form.body;
      if (form.cta_text) updates.cta_text = form.cta_text;
      if (form.cta_color) updates.cta_color = form.cta_color;

      const saved = await upsertTenantEmailTemplate(tenant.id, templateKey, updates);
      setOverrides(prev => {
        const filtered = prev.filter(o => o.template_key !== templateKey);
        return [...filtered, saved];
      });
    } catch (err) {
      setError((err as Error).message);
    }
    setSaving(null);
  };

  const handleReset = async (templateKey: string) => {
    setSaving(templateKey);
    setError(null);
    try {
      await deleteTenantEmailTemplate(tenant.id, templateKey);
      setOverrides(prev => prev.filter(o => o.template_key !== templateKey));
      setEditForm(prev => ({
        ...prev,
        [templateKey]: {
          subject: '',
          heading: '',
          subtitle: '',
          body: '',
          cta_text: '',
          cta_color: '',
        },
      }));
    } catch (err) {
      setError((err as Error).message);
    }
    setSaving(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-brand-600 dark:text-brand-400" size={24} />
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="text-center py-16">
        <FileText size={32} className="mx-auto text-surface-400 mb-3" />
        <p className="text-sm text-surface-500">No templates available.</p>
        <p className="text-xs text-surface-400 mt-1">
          Email templates have not been configured by the platform yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <p className="text-xs text-surface-500">
        Customize the content of your email templates. Leave fields empty to use the platform defaults.
      </p>

      {error && (
        <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
          <X size={12} /> {error}
        </p>
      )}

      <div className="glass-panel overflow-hidden divide-y divide-surface-200">
        {templates.map(template => {
          const key = template.template_key;
          const override = getOverride(key);
          const isExpanded = expandedKey === key;
          const isSaving = saving === key;
          const form = editForm[key] || {};

          return (
            <div key={template.id}>
              {/* Row header */}
              <button
                onClick={() => handleExpand(key)}
                className="w-full flex items-center justify-between gap-4 px-5 py-3.5 text-left hover:bg-surface-50 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-surface-900">
                      {template.display_name}
                    </span>
                    {override && (
                      <span className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full font-medium bg-brand-50 text-brand-600">
                        <Palette size={10} className="mr-1" />
                        Customized
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-surface-500 mt-0.5 line-clamp-1">
                    {template.description}
                  </p>
                </div>
                {isExpanded ? (
                  <ChevronUp size={16} className="text-surface-400 shrink-0" />
                ) : (
                  <ChevronDown size={16} className="text-surface-400 shrink-0" />
                )}
              </button>

              {/* Expanded editor */}
              {isExpanded && (
                <div className="px-5 pb-5 pt-2 space-y-4 bg-surface-50/50">
                  {/* Subject */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-surface-700">Subject Line</label>
                    <input
                      type="text"
                      value={form.subject || ''}
                      onChange={e => updateField(key, 'subject', e.target.value)}
                      placeholder={template.subject_template || 'Default subject'}
                      className="input-field w-full"
                    />
                  </div>

                  {/* Heading */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-surface-700">Heading</label>
                    <input
                      type="text"
                      value={form.heading || ''}
                      onChange={e => updateField(key, 'heading', e.target.value)}
                      placeholder={template.default_heading || 'Default heading'}
                      className="input-field w-full"
                    />
                  </div>

                  {/* Subtitle */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-surface-700">Subtitle</label>
                    <input
                      type="text"
                      value={form.subtitle || ''}
                      onChange={e => updateField(key, 'subtitle', e.target.value)}
                      placeholder={template.default_subtitle || 'Default subtitle'}
                      className="input-field w-full"
                    />
                  </div>

                  {/* Body */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-surface-700">Body</label>
                    <textarea
                      value={form.body || ''}
                      onChange={e => updateField(key, 'body', e.target.value)}
                      placeholder={template.default_body || 'Default body text'}
                      rows={4}
                      className="input-field w-full resize-y"
                    />
                  </div>

                  {/* CTA Text */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-surface-700">CTA Text</label>
                    <input
                      type="text"
                      value={form.cta_text || ''}
                      onChange={e => updateField(key, 'cta_text', e.target.value)}
                      placeholder={template.cta_text || 'View Dashboard'}
                      className="input-field w-full"
                    />
                  </div>

                  {/* CTA Color */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-surface-700">CTA Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={form.cta_color || template.cta_color || '#2AB3B1'}
                        onChange={e => updateField(key, 'cta_color', e.target.value)}
                        className="w-8 h-8 rounded border border-surface-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={form.cta_color || ''}
                        onChange={e => updateField(key, 'cta_color', e.target.value)}
                        placeholder={template.cta_color || '#2AB3B1'}
                        className="input-field flex-1 font-mono text-sm"
                      />
                    </div>
                  </div>

                  {/* Available Variables */}
                  {template.available_variables && template.available_variables.length > 0 && (
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-surface-700">Available Variables</label>
                      <div className="flex flex-wrap gap-1.5">
                        {template.available_variables.map(v => (
                          <span
                            key={v}
                            className="inline-flex items-center text-[10px] px-2 py-1 rounded-full bg-brand-50 text-brand-600 font-mono"
                          >
                            {`{{${v}}}`}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-3 pt-2">
                    <button
                      onClick={() => handleSave(key)}
                      disabled={isSaving}
                      className={cn(
                        'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
                        'bg-brand-500 hover:bg-brand-600 text-white',
                        isSaving && 'opacity-60 cursor-not-allowed',
                      )}
                    >
                      {isSaving ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Save size={14} />
                      )}
                      Save
                    </button>
                    {override && (
                      <button
                        onClick={() => handleReset(key)}
                        disabled={isSaving}
                        className={cn(
                          'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
                          'bg-surface-200 hover:bg-surface-300 text-surface-700',
                          isSaving && 'opacity-60 cursor-not-allowed',
                        )}
                      >
                        <RotateCcw size={14} />
                        Reset to Default
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// TAB 4 -- Campaigns
// =============================================================================

const STATUS_BADGE: Record<string, string> = {
  draft:     'bg-surface-200 text-surface-600',
  scheduled: 'bg-blue-100 text-blue-700',
  sending:   'bg-yellow-100 text-yellow-700',
  sent:      'bg-emerald-100 text-emerald-700',
  failed:    'bg-red-100 text-red-700',
  cancelled: 'bg-surface-200 text-surface-500',
};

function CampaignsTab({ tenant }: { tenant: Tenant }) {
  const [view, setView] = useState<'list' | 'compose' | 'stats'>('list');
  const [campaigns, setCampaigns] = useState<CampaignEmail[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignEmail | null>(null);
  const [loading, setLoading] = useState(true);
  const [campaignsThisMonth, setCampaignsThisMonth] = useState(0);

  const limits = {
    maxPerMonth: tenant.plan?.max_campaigns_per_month ?? 0,
    maxRecipients: tenant.plan?.max_campaign_recipients ?? 0,
  };
  const featureEnabled = tenant.features?.email_campaigns ?? false;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, count] = await Promise.all([
        fetchCampaigns(tenant.id),
        fetchCampaignsThisMonth(tenant.id),
      ]);
      setCampaigns(list);
      setCampaignsThisMonth(count);
    } catch (err) {
      console.error('Failed to load campaigns:', err);
    }
    setLoading(false);
  }, [tenant.id]);

  useEffect(() => { load(); }, [load]);

  // Feature gate
  if (!featureEnabled) {
    return (
      <div className="glass-panel p-8 text-center max-w-lg mx-auto">
        <AlertCircle size={36} className="mx-auto text-surface-400 mb-3" />
        <p className="text-sm font-medium text-surface-700">Email Campaigns</p>
        <p className="text-xs text-surface-500 mt-2">
          Send batch emails to your B2C users. Available on Pro, Business, and Enterprise plans.
        </p>
        <p className="text-xs text-surface-400 mt-1">
          Upgrade your plan to unlock this feature.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-brand-600 dark:text-brand-400" size={24} />
      </div>
    );
  }

  if (view === 'compose') {
    return (
      <ComposeView
        tenant={tenant}
        limits={limits}
        onBack={() => { setView('list'); load(); }}
      />
    );
  }

  if (view === 'stats' && selectedCampaign) {
    return (
      <StatsView
        campaign={selectedCampaign}
        onBack={() => { setView('list'); load(); }}
      />
    );
  }

  // List view
  const canCreate = limits.maxPerMonth === -1 || campaignsThisMonth < limits.maxPerMonth;

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          {limits.maxPerMonth > 0 && (
            <p className="text-xs text-surface-500">
              {campaignsThisMonth} of {limits.maxPerMonth} campaigns used this month
            </p>
          )}
          {limits.maxPerMonth === -1 && (
            <p className="text-xs text-surface-500">
              {campaignsThisMonth} campaign{campaignsThisMonth !== 1 ? 's' : ''} sent this month (unlimited)
            </p>
          )}
        </div>
        <button
          onClick={() => setView('compose')}
          disabled={!canCreate}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
            canCreate
              ? 'bg-brand-500 hover:bg-brand-600 text-white'
              : 'bg-surface-200 text-surface-400 cursor-not-allowed',
          )}
        >
          <Plus size={14} />
          New Campaign
        </button>
      </div>

      {campaigns.length === 0 ? (
        <div className="glass-panel p-8 text-center">
          <Send size={32} className="mx-auto text-surface-400 mb-3" />
          <p className="text-sm text-surface-500">No campaigns yet</p>
          <p className="text-xs text-surface-400 mt-1">
            Create your first campaign to reach your users.
          </p>
        </div>
      ) : (
        <div className="glass-panel overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-200 bg-surface-50">
                <th className="px-5 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-surface-500">Subject</th>
                <th className="px-5 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-surface-500">Status</th>
                <th className="px-5 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-surface-500">Recipients</th>
                <th className="px-5 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-surface-500">Sent At</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-200">
              {campaigns.map(c => (
                <tr
                  key={c.id}
                  onClick={() => { setSelectedCampaign(c); setView('stats'); }}
                  className="hover:bg-surface-50 cursor-pointer transition-colors"
                >
                  <td className="px-5 py-3 font-medium text-surface-900 truncate max-w-[240px]">
                    {c.subject}
                  </td>
                  <td className="px-5 py-3">
                    <span className={cn('text-[10px] px-2 py-0.5 rounded-full font-medium', STATUS_BADGE[c.status] || STATUS_BADGE.draft)}>
                      {c.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-surface-600">
                    <span className="flex items-center gap-1">
                      <Users size={12} />
                      {c.recipient_count}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-surface-500 text-xs">
                    {c.sent_at ? (
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {new Date(c.sent_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    ) : (
                      <span className="text-surface-400">--</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// -- Compose View -------------------------------------------------------------

function ComposeView({
  tenant,
  limits,
  onBack,
}: {
  tenant: Tenant;
  limits: { maxPerMonth: number; maxRecipients: number };
  onBack: () => void;
}) {
  const [subject, setSubject] = useState('');
  const [heading, setHeading] = useState('');
  const [body, setBody] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [recipientFilter, setRecipientFilter] = useState<string>('all');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSending(true);
    setError(null);
    try {
      const campaign = await createCampaign(tenant.id, {
        subject: subject.trim(),
        heading: heading.trim() || undefined,
        body: body.trim(),
        cta_text: ctaText.trim() || undefined,
        cta_url: ctaUrl.trim() || undefined,
        recipient_filter: recipientFilter as CreateCampaignInput['recipient_filter'],
      });
      await sendCampaignNow(campaign.id);
      onBack();
    } catch (err) {
      setError((err as Error).message);
    }
    setSending(false);
  };

  const handleSaveDraft = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSending(true);
    setError(null);
    try {
      await createCampaign(tenant.id, {
        subject: subject.trim(),
        heading: heading.trim() || undefined,
        body: body.trim(),
        cta_text: ctaText.trim() || undefined,
        cta_url: ctaUrl.trim() || undefined,
        recipient_filter: recipientFilter as CreateCampaignInput['recipient_filter'],
      });
      onBack();
    } catch (err) {
      setError((err as Error).message);
    }
    setSending(false);
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-surface-500 hover:text-surface-700 transition-colors"
      >
        <ChevronUp size={14} className="rotate-[-90deg]" />
        Back to Campaigns
      </button>

      <div className="glass-panel p-6 space-y-5">
        <h2 className="text-lg font-semibold text-surface-900">New Campaign</h2>

        {/* Subject */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-surface-700">
            Subject <span className="text-red-600 dark:text-red-400">*</span>
          </label>
          <input
            type="text"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="Email subject line"
            className="input-field w-full"
          />
        </div>

        {/* Heading */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-surface-700">Heading</label>
          <input
            type="text"
            value={heading}
            onChange={e => setHeading(e.target.value)}
            placeholder="Optional heading"
            className="input-field w-full"
          />
        </div>

        {/* Body */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-surface-700">
            Body <span className="text-red-600 dark:text-red-400">*</span>
          </label>
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Write your email content..."
            rows={6}
            className="input-field w-full resize-y"
          />
        </div>

        {/* CTA row */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-surface-700">CTA Text</label>
            <input
              type="text"
              value={ctaText}
              onChange={e => setCtaText(e.target.value)}
              placeholder="e.g. Learn More"
              className="input-field w-full"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-surface-700">CTA URL</label>
            <input
              type="url"
              value={ctaUrl}
              onChange={e => setCtaUrl(e.target.value)}
              placeholder="https://..."
              className="input-field w-full"
            />
          </div>
        </div>

        {/* Recipient filter */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-surface-700">Recipients</label>
          <select
            value={recipientFilter}
            onChange={e => setRecipientFilter(e.target.value)}
            className="input-field w-full py-1.5 text-sm"
          >
            {Object.entries(CAMPAIGN_FILTER_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <p className="text-[10px] text-surface-400">
            Up to {limits.maxRecipients} recipients per campaign
          </p>
        </div>

        {/* Error */}
        {error && (
          <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
            <X size={12} /> {error}
          </p>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={handleSend}
            disabled={sending || !subject.trim() || !body.trim()}
            className={cn(
              'flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all',
              'bg-brand-500 hover:bg-brand-600 text-white',
              (sending || !subject.trim() || !body.trim()) && 'opacity-60 cursor-not-allowed',
            )}
          >
            {sending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Send size={14} />
            )}
            Send Now
          </button>
          <button
            onClick={handleSaveDraft}
            disabled={sending || !subject.trim() || !body.trim()}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
              'bg-surface-200 hover:bg-surface-300 text-surface-700',
              (sending || !subject.trim() || !body.trim()) && 'opacity-60 cursor-not-allowed',
            )}
          >
            <Save size={14} />
            Save as Draft
          </button>
        </div>
      </div>
    </div>
  );
}

// -- Stats View ---------------------------------------------------------------

function StatsView({
  campaign,
  onBack,
}: {
  campaign: CampaignEmail;
  onBack: () => void;
}) {
  const [recipients, setRecipients] = useState<CampaignRecipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const list = await fetchCampaignRecipients(campaign.id);
        setRecipients(list);
      } catch (err) {
        console.error('Failed to load recipients:', err);
      }
      setLoading(false);
    })();
  }, [campaign.id]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await cancelCampaign(campaign.id);
      onBack();
    } catch (err) {
      console.error('Failed to cancel campaign:', err);
    }
    setCancelling(false);
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-surface-500 hover:text-surface-700 transition-colors"
      >
        <ChevronUp size={14} className="rotate-[-90deg]" />
        Back to Campaigns
      </button>

      {/* Campaign header */}
      <div className="glass-panel p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-surface-900">{campaign.subject}</h2>
            <div className="flex items-center gap-3 mt-2">
              <span className={cn('text-[10px] px-2 py-0.5 rounded-full font-medium', STATUS_BADGE[campaign.status] || STATUS_BADGE.draft)}>
                {campaign.status}
              </span>
              {campaign.sent_at && (
                <span className="text-xs text-surface-500 flex items-center gap-1">
                  <Clock size={12} />
                  {new Date(campaign.sent_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
          </div>
          {(campaign.status === 'draft' || campaign.status === 'scheduled') && (
            <button
              onClick={handleCancel}
              disabled={cancelling}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
                'bg-red-50 hover:bg-red-100 text-red-600',
                cancelling && 'opacity-60 cursor-not-allowed',
              )}
            >
              {cancelling ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
              Cancel
            </button>
          )}
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-4 pt-2">
          <div className="text-center p-3 rounded-lg bg-surface-50">
            <p className="text-2xl font-bold text-surface-900">{campaign.recipient_count}</p>
            <p className="text-[10px] font-medium uppercase tracking-wider text-surface-500 mt-1">Total</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-surface-50">
            <p className="text-2xl font-bold text-emerald-600">{campaign.sent_count}</p>
            <p className="text-[10px] font-medium uppercase tracking-wider text-surface-500 mt-1">Sent</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-surface-50">
            <p className="text-2xl font-bold text-red-500">{campaign.failed_count}</p>
            <p className="text-[10px] font-medium uppercase tracking-wider text-surface-500 mt-1">Failed</p>
          </div>
        </div>
      </div>

      {/* Recipients */}
      <div className="glass-panel overflow-hidden">
        <div className="px-5 py-2.5 bg-surface-50 border-b border-surface-200">
          <span className="text-[10px] font-bold uppercase tracking-wider text-surface-500">
            Recipients
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="animate-spin text-brand-600 dark:text-brand-400" size={20} />
          </div>
        ) : recipients.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-xs text-surface-400">No recipients yet.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-200">
                <th className="px-5 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-surface-500">Name</th>
                <th className="px-5 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-surface-500">Email</th>
                <th className="px-5 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-surface-500">Status</th>
                <th className="px-5 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-surface-500">Sent At</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-200">
              {recipients.map(r => (
                <tr key={r.id}>
                  <td className="px-5 py-2.5 text-surface-900">{r.full_name || '--'}</td>
                  <td className="px-5 py-2.5 text-surface-600">{r.email}</td>
                  <td className="px-5 py-2.5">
                    <span className={cn(
                      'text-[10px] px-2 py-0.5 rounded-full font-medium',
                      r.status === 'sent' ? 'bg-emerald-100 text-emerald-700' :
                      r.status === 'failed' ? 'bg-red-100 text-red-700' :
                      r.status === 'skipped' ? 'bg-surface-200 text-surface-500' :
                      'bg-yellow-100 text-yellow-700',
                    )}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-5 py-2.5 text-xs text-surface-500">
                    {r.sent_at
                      ? new Date(r.sent_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : '--'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// TAB 5 -- Preview
// =============================================================================

function PreviewTab({ tenant }: { tenant: Tenant }) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [overrides, setOverrides] = useState<TenantEmailTemplate[]>([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [all, tenantOverrides] = await Promise.all([
          fetchEmailTemplates(),
          fetchTenantEmailTemplates(tenant.id),
        ]);
        // Only show tenant-visible types
        setTemplates(all.filter(t => t.tenant_visible === true && (t.recipient_type === 'tenant_admin' || t.recipient_type === 'end_user')));
        setOverrides(tenantOverrides);
      } catch (err) {
        console.error('Failed to load email templates:', err);
      }
      setLoading(false);
    })();
  }, [tenant.id]);

  const branding = tenant.branding;
  const appName = branding?.app_name || tenant.name;
  const primaryColor = branding?.primary_color || '#2AB3B1';
  const logoUrl = branding?.logo_url;
  const companyName = branding?.company_name;

  const SAMPLE_VARS: Record<string, Record<string, string>> = {
    booking_inquiry: { user_name: 'Jane Doe', user_email: 'jane@example.com', bulletin_title: 'Bali Adventure Tour', destinations: 'Bali, Indonesia', user_phone: '+1 555-0123', group_size: '4' },
    bulletin_interest: { user_name: 'John Smith', user_email: 'john@example.com', bulletin_title: 'Tokyo Cultural Experience', destinations: 'Tokyo, Japan' },
    contact_inquiry: { category_label: 'General', name: 'Alex Chen', email: 'alex@example.com', subject: 'Partnership Inquiry', message: 'I would love to discuss a potential collaboration.' },
    admin_invite_new: { tenant_name: appName, app_name: appName, role_label: 'Admin', invite_link: 'https://example.com/accept' },
    admin_invite_existing: { tenant_name: appName, app_name: appName, role_label: 'Admin' },
    business_lead: { app_name: appName, full_name: 'Sarah Johnson', type_label: 'Travel Agency', email: 'sarah@agency.com', phone: '+1 555-0456', business_name: 'Wanderlust Travel' },
    user_signup: { user_name: 'New Traveler', user_email: 'traveler@example.com', tenant_name: appName, current_count: '42', max_users: '50' },
    billing_payment_failed: { tenant_name: appName },
    billing_subscription: { tenant_name: appName, plan_name: 'Pro', event: 'upgraded' },
    weekly_digest: { tenant_name: appName, date_range: 'Mar 3 \u2013 Mar 9', current_users: '42', max_users: '50', new_inquiries: '5', active_bulletins: '8', seat_percent: '84', storage_used: '2.1 GB', storage_limit: '5 GB', storage_percent: '42' },
    trip_invitation: { inviter_name: 'Jane Doe', trip_name: 'Bali, Indonesia', app_name: appName, invite_link: 'https://example.com/invite/abc123' },
    zoom_meeting: { organizer_name: 'Jane Doe', trip_name: 'Tokyo, Japan', topic: 'Trip Planning Call', meeting_link: 'https://zoom.us/j/123', meeting_time: 'March 20, 2026 at 3:00 PM' },
  };

  function buildPreviewHtml(): string {
    if (!selectedKey) return '';

    const template = templates.find(t => t.template_key === selectedKey);
    const override = overrides.find(o => o.template_key === selectedKey);
    if (!template) return '';

    // Merge: override -> default
    const heading = override?.heading || template.default_heading || template.display_name;
    const subtitle = override?.subtitle || template.default_subtitle || '';
    const body = override?.body || template.default_body || '';
    const ctaText = override?.cta_text || template.cta_text || 'View Dashboard';
    const ctaColor = override?.cta_color || template.cta_color || primaryColor;

    const vars = SAMPLE_VARS[selectedKey] || {};
    const interp = (str: string) => str.replace(/\{\{(\w+)\}\}/g, (_, k) => vars[k] ?? `{{${k}}}`);

    const logoHtml = logoUrl
      ? `<img src="${logoUrl}" alt="${appName}" style="max-height: 36px; max-width: 180px; margin-bottom: 8px;" />`
      : '';

    return `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #0f172a;">
  <div style="max-width: 600px; margin: 0 auto; padding: 40px 20px;">
    <div style="text-align: center; margin-bottom: 32px;">
      ${logoHtml}
      <h1 style="color: #f8fafc; font-size: 24px; margin: 0; font-weight: 700;">${appName}</h1>
      <p style="color: #64748b; font-size: 11px; letter-spacing: 2px; text-transform: uppercase; margin: 4px 0 0 0;">Notifications</p>
      <div style="width: 40px; height: 3px; background-color: ${primaryColor}; margin: 12px auto 0; border-radius: 2px;"></div>
    </div>
    <div style="background-color: #1e293b; border: 1px solid #334155; border-radius: 16px; padding: 40px 32px;">
      <h2 style="color: #f8fafc; font-size: 18px; font-weight: 600; margin: 0 0 8px 0;">${interp(heading)}</h2>
      ${subtitle ? `<p style="color: #94a3b8; font-size: 13px; margin: 0 0 20px 0;">${interp(subtitle)}</p>` : ''}
      ${body ? `<p style="color: #e2e8f0; font-size: 14px; line-height: 1.6; margin: 0 0 16px 0;">${interp(body)}</p>` : ''}
      <p style="color: #94a3b8; font-size: 13px; line-height: 1.6;">
        <em>Email content and details will appear here based on the notification type.</em>
      </p>
      <div style="text-align: center; margin-top: 28px;">
        <a href="#" style="display: inline-block; background-color: ${ctaColor}; color: #ffffff; text-decoration: none; padding: 12px 28px; border-radius: 8px; font-size: 14px; font-weight: 600;">
          ${interp(ctaText)}
        </a>
      </div>
    </div>
    <div style="text-align: center; margin-top: 32px;">
      <p style="color: #475569; font-size: 12px; margin: 0;">${companyName ? `Sent by ${companyName}.` : `Sent by ${appName}.`}</p>
      <p style="color: #475569; font-size: 11px; margin: 8px 0 0 0;">Manage your notification preferences in your dashboard settings.</p>
    </div>
  </div>
</body>
</html>`;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-brand-600 dark:text-brand-400" size={24} />
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-3xl">
      {/* Selector */}
      <div className="max-w-sm space-y-1.5">
        <label className="text-xs font-medium text-surface-700">Email Type</label>
        <select
          value={selectedKey}
          onChange={e => setSelectedKey(e.target.value)}
          className="input-field w-full py-1.5 text-sm"
        >
          <option value="">Select an email type...</option>
          {templates.map(t => (
            <option key={t.template_key} value={t.template_key}>{t.display_name}</option>
          ))}
        </select>
      </div>

      {/* Preview area */}
      {selectedKey ? (
        <iframe
          srcDoc={buildPreviewHtml()}
          sandbox=""
          title="Email Preview"
          className="w-full border border-surface-200 rounded-xl"
          style={{ minHeight: 500 }}
        />
      ) : (
        <div className="glass-panel p-8 min-h-[280px] flex flex-col items-center justify-center text-center">
          <Eye size={36} className="mx-auto text-surface-300" />
          <p className="text-sm text-surface-500 mt-3">
            Select an email type above to preview
          </p>
          <p className="text-xs text-surface-400 max-w-sm mt-1">
            Email preview shows how your branded emails appear to recipients
          </p>
        </div>
      )}
    </div>
  );
}
